import { HelpTopic } from './helpContent';

/**
 * Help content for the Multi Analysis page. Explains the batch workflow, how
 * each methodology produces its fit, and — most importantly — the Valid /
 * Review / Failed validation criteria and why each is assigned. The status
 * copy mirrors the real rules in the application.
 */
export const multiAnalysisHelpTopics: HelpTopic[] = [
  {
    id: 'ma-workflow',
    label: 'Workflow',
    title: 'How Multi Analysis works',
    intro:
      'Multi Analysis runs the same capillary-pressure and relative-permeability calculations as the single-sample pages, but over many samples at once, with a per-sample quality status so you can spot the ones that need attention. Nothing is written to the project until you explicitly save.',
    sections: [
      {
        id: 'ma-flow',
        title: 'Select → Preview → Review → Save',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Select samples — pick a well or all samples of the project, or choose them individually. Each selected sample becomes a card in the grid.',
              'Choose the methodology (Liao / Brooks-Corey / Water-Oil Kr / Gas-Oil Kr) with the toggle on the right. Each methodology keeps its own batch, so switching back and forth never loses results.',
              'Preview — computes every checked sample and shows its fit and status in the grid. This is a dry run: it does NOT modify the stored project.',
              'Review — read the per-sample status (Valid / Review / Failed), open any card for the full chart, and mark cards as reviewed.',
              'Save reviewed — re-runs the checked, non-failed samples and saves the reviewed results to the project.',
            ],
          },
          {
            type: 'note',
            title: 'Nothing is saved until you Save',
            text: 'Preview is non-destructive — it never overwrites stored results or the manual refinements you may have made on the single-sample pages. Only "Save reviewed" writes.',
          },
          {
            type: 'note',
            title: 'Overwrites are guarded',
            text: 'If any sample you are about to save already has a stored result for this methodology, a confirmation appears first ("Overwrite existing analyses?") so a batch never silently replaces earlier work.',
          },
        ],
      },
      {
        id: 'ma-filters',
        title: 'Filtering the grid',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Show (categories) — Capillary Pressure, Kr Analysis, No data. A sample appears under a category if it has the matching input data or a stored result. These are multi-select and just control what is visible.',
              'Status filters — All, Valid, Review, Failed, Unreviewed. Unreviewed means a sample has no result yet, or you have not ticked it as reviewed.',
              'Show and status filters are view-only: they never change Preview, Save reviewed, or selection scope. Select All selects every loaded sample; Rerun failed re-runs only failed results; Cancel stops a running batch.',
            ],
          },
          {
            type: 'paragraph',
            text: 'The bottom bar shows an aggregate fit: Mean R² for Brooks-Corey and Kr, Mean RMSE for Liao.',
          },
        ],
      },
    ],
  },
  {
    id: 'ma-methodologies',
    label: 'Methodologies',
    title: 'How each methodology is computed',
    intro:
      'The four methodologies fit different models to the lab measurements. The batch applies exactly the same math as the single-sample pages — here is what each one does.',
    sections: [
      {
        id: 'ma-liao',
        title: 'Liao (capillary pressure)',
        blocks: [
          {
            type: 'paragraph',
            text: 'Liao fits a three-parameter capillary-pressure model. With Pce on Auto, an optimizer jointly searches for a, b and normalized entry pressure Pce to match the corrected lab Pc-vs-Sw points; lambda is then derived from those values. It is a curve-fit parameterization, so the plotted curve and the quality flags still need specialist review.',
          },
          {
            type: 'bullets',
            items: [
              'Pce is optimized automatically when the card is left on Auto. A positive existing or entered Pce is kept fixed as an expert override; an explicitly entered zero is also fixed and flagged for review.',
              'Automatic Pce is constrained to 1e-6 through the smallest positive measured normalized PcD (and always below 1). This prevents a zero entry pressure or an entry pressure above the first positive measured pressure. Reaching either bound is a Review flag, not an automatic rejection.',
              'a is bounded to 0.000001–1 and b to 1–1000. A value at a bound means the data preferred to continue beyond the search interval; it is a sensitivity signal to inspect, not a measured geological property.',
              'The fit quality is reported as an RMSE (lower is better).',
            ],
          },
        ],
      },
      {
        id: 'ma-brooks-corey',
        title: 'Brooks-Corey',
        blocks: [
          {
            type: 'paragraph',
            text: 'Brooks-Corey fits Pc = Pe · Swn^m, where Pe is entry pressure and Swn is normalized water saturation. The fitted log-log exponent m is negative for normal drainage data, so Pc rises as Sw approaches Swir. The classic positive Brooks-Corey pore-size distribution index is λBC = −1/m.',
          },
          {
            type: 'paragraph',
            text: 'In batch mode the point subset is chosen automatically per sample: the largest run of consecutive measured points that gives a good fit is used (see the Statuses tab for the exact rule). Points that do not belong to a clean drainage trend are left out so a single bad reading does not distort the whole curve.',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Mean slope',
                description:
                  'Averages the slope measured between each point and the entry pressure.',
                effect:
                  'The original Brooks-Corey averaging approach; simple but can be unstable near full water saturation.',
              },
              {
                name: 'OLS log-log',
                description:
                  'One straight-line least-squares fit through all points on a log-log plot.',
                effect: 'Stable and uses every point jointly.',
              },
              {
                name: 'Nonlinear least-squares (recommended)',
                description:
                  'Fits the curve directly to the measured pressures.',
                effect:
                  'Usually the closest overall match and lowest absolute fitting error. Mean slope remains the default for backward compatibility with existing saved analyses.',
              },
              {
                name: 'Weighted nonlinear least-squares',
                description:
                  'Nonlinear fit that weights low- and high-pressure points evenly.',
                effect: 'Best when the pressures span a very wide range.',
              },
            ],
          },
          {
            type: 'note',
            title: 'Compare all methods',
            text: 'Turn this on to fit all four solvers for every sample and let the app auto-pick the lowest-error one per sample (about 4x slower to preview). Open a sample to see the side-by-side comparison and override the choice.',
          },
        ],
      },
      {
        id: 'ma-kr',
        title: 'Water-Oil Kr / Gas-Oil Kr',
        blocks: [
          {
            type: 'paragraph',
            text: 'The Kr methodologies fit Corey-type relative-permeability curves to the lab Kr measurements. A run mode controls how the endpoints and exponents are chosen.',
          },
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Auto refinement (default)',
                description:
                  'The app searches for the endpoints and exponents that best fit the measured Kr points.',
                effect:
                  'Best fit; the refined result feeds the correlation the same way a manual refinement does.',
              },
              {
                name: 'Lab baseline',
                description:
                  'Uses the lab-provided values without additional refinement.',
                effect: 'A faithful baseline when you trust the lab inputs as-is.',
              },
            ],
          },
          {
            type: 'paragraph',
            text: 'Auto refinement first selects stable regression windows, estimates true residual saturation and extrapolated endpoints, then derives refined Corey exponents from a low-variation plateau. It writes the calculated true_* values used by Auto Correlation. Existing manual refinement is deliberately preserved unless you explicitly confirm overwrite on Save.',
          },
        ],
      },
    ],
  },
  {
    id: 'ma-statuses',
    label: 'Statuses',
    title: 'Valid, Review and Failed — and why',
    intro:
      'Every previewed sample gets one of three statuses. The status is a quality gauge, not a verdict — a fit that needs a closer look is flagged Review, not hidden.',
    sections: [
      {
        id: 'ma-status-meaning',
        title: 'What each status means',
        blocks: [
          {
            type: 'parameterGrid',
            items: [
              {
                name: 'Valid',
                description:
                  'The calculation succeeded and passed every quality check.',
                effect: 'Safe to save as-is.',
              },
              {
                name: 'Review',
                description:
                  'The calculation succeeded but at least one quality check flagged something.',
                effect:
                  'Open the sample and check the fit before saving; it is still saveable.',
              },
              {
                name: 'Failed',
                description:
                  'The calculation could not produce a valid result (bad or missing inputs, or no usable point subset).',
                effect: 'Cannot be saved — fix the inputs and rerun.',
              },
            ],
          },
          {
            type: 'note',
            title: 'Review is not "bad"',
            text: 'A Review sample still has a computed curve shown on its card. The flag simply means "look at this one" — the fit may be perfectly acceptable once you have checked it.',
          },
        ],
      },
      {
        id: 'ma-why-review-bc',
        title: 'Why a Brooks-Corey sample is flagged Review',
        blocks: [
          {
            type: 'bullets',
            items: [
              'No point subset reached the preferred fit criteria — a fit needs R² ≥ 0.90 AND NRMSE ≤ 0.20 to be Valid; the best available fit was below that.',
              'A point was automatically excluded — the auto-selection dropped one internal outlier (a point far off the trend) or fell back to fewer points to get a usable fit.',
              'The reconstructed curve overshoots the data — the fit matches the measured points but the curve explodes near irreducible water (peak more than ~100x the largest measured Pc), usually meaning the input Sw/Pc are off. The curve is still shown so you can inspect it.',
              'Few regression points, a non-positive entry pressure, or an unexpected lambda sign.',
            ],
          },
          {
            type: 'note',
            title: 'Inferred Swi',
            text: '"Swi missing — swir inferred from the minimum measured Sw" is informational only. It is shown so you know the irreducible saturation was estimated, but it never by itself makes a sample Review.',
          },
        ],
      },
      {
        id: 'ma-why-review-liao',
        title: 'Why a Liao sample is flagged Review',
        blocks: [
          {
            type: 'bullets',
            items: [
              'High fit error — RMSE greater than 0.05.',
              'Few valid fit points — fewer than 5 usable measurements.',
              'A parameter is stuck at an optimizer bound — a or b pinned at the edge of its search range, meaning the optimizer could not settle inside it.',
              'Automatic Pce reached its data-derived lower or upper bound, or a Pce was explicitly fixed at zero.',
              'Missing Swir, or missing FZI (which would make the correlation skip the sample).',
            ],
          },
        ],
      },
      {
        id: 'ma-why-review-kr',
        title: 'Why a Kr sample is flagged Review',
        blocks: [
          {
            type: 'bullets',
            items: [
              'The selected oil- or water/gas-endpoint regression is borderline (R² from 0.90 up to, but not including, 0.98), or its exponent plateau has a high coefficient of variation (CV).',
              'The refined exponent sum is outside the screening band: No+Nw or No+Ng below 7.0 or above 8.5. This is a screening heuristic, not a universal physical law.',
              'For water-oil, Kro=Krw crosses below Sw=0.5, which is a Craig-rule wettability prompt. For gas-oil, Swi + Sorg + maximum Sg materially differs from 1.',
              'The refined Nw conflicts with the sample Brooks-Corey lambda cross-check, or a prior manual refinement was preserved rather than recalculated.',
            ],
          },
          {
            type: 'note',
            title: 'How to use these metrics',
            text: 'A Kr Review result is calculated and may be appropriate for the core. Check the selected points, endpoint extrapolation, local exponent plateau, saturation balance and wettability context before saving.',
          },
        ],
      },
      {
        id: 'ma-why-failed',
        title: 'Why a sample Fails',
        blocks: [
          {
            type: 'bullets',
            items: [
              'The inputs are invalid or insufficient — e.g. missing capillary-pressure points, an unresolvable Swir, an invalid Pce, salinity or interfacial-tension values, or too few valid points to fit.',
              'Brooks-Corey found no valid point subset at all.',
              'The request itself errored.',
            ],
          },
          {
            type: 'paragraph',
            text: 'Failed samples are never saved. Fix the sample data (on the single-sample page or via the measured-data table) and rerun.',
          },
        ],
      },
    ],
  },
  {
    id: 'ma-reading',
    label: 'Reading a result',
    title: 'Reading the grid and the detail view',
    intro:
      'Each card summarizes a sample; open one for the full chart and the tools to accept or refine it.',
    sections: [
      {
        id: 'ma-card',
        title: 'The sample card',
        blocks: [
          {
            type: 'bullets',
            items: [
              'Key parameters (e.g. Pe, lambda, R², RMSE) and the number of points used (used / total).',
              'A small fit preview with the measured points and the fitted curve.',
              'Tags: Preview vs Saved, "Overwrites saved" (a stored result would be replaced), "Outlier excluded" (a point was auto-removed), and the solver used.',
            ],
          },
        ],
      },
      {
        id: 'ma-detail',
        title: 'The detail view',
        blocks: [
          {
            type: 'bullets',
            items: [
              'A full fit-vs-lab chart with a Linear / Logarithmic toggle.',
              'For Brooks-Corey with "Compare all methods" on: a solver comparison table judged by Fit RMSE, NRMSE and Max residual (not R², which is the same across solvers) — with the best badged and selectable.',
              'Rerun this sample, mark it reviewed, and "Open in Capillary Pressure" / "Open in Kr Analysis" to refine it by hand on the single-sample page.',
            ],
          },
        ],
      },
    ],
  },
];
